import React, {useEffect, useState} from 'react'
import axios from 'axios'
import toaster from '../../../../Utils/toaster'
import {useFeatureAccess} from '../../../hooks/useFeatureAccess'
import {DeleteConfirmationModal} from '../../../common/Models/confirmation-modal/DeleteConfirmationModal'

const API_URL = process.env.REACT_APP_API_URL

type FrequencyEntry = {
  id?: number
  hoursBeforeAppointment: number
}

type TypeSettings = {
  smsEnabled: boolean
  emailEnabled: boolean
  frequencies: FrequencyEntry[]
}

type SettingsResponse = {
  isEnabled: boolean
  osi: TypeSettings
  vis: TypeSettings
}

type FrequencyRowState = FrequencyEntry & {
  clientKey: string
  isEditing: boolean
}

const createClientKey = () => `freq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const mapFrequencies = (frequencies: FrequencyEntry[]): FrequencyRowState[] => {
  if (!frequencies.length) {
    return [{clientKey: createClientKey(), hoursBeforeAppointment: 24, isEditing: false}]
  }

  return frequencies.map((frequency) => ({
    ...frequency,
    clientKey: frequency.id ? `saved-${frequency.id}` : createClientKey(),
    isEditing: false,
  }))
}

function FrequencySection({
  title,
  helperText,
  smsEnabled,
  emailEnabled,
  frequencies,
  canEdit,
  onSmsChange,
  onEmailChange,
  onFrequenciesChange,
}: {
  title: string
  helperText: string
  smsEnabled: boolean
  emailEnabled: boolean
  frequencies: FrequencyRowState[]
  canEdit: boolean
  onSmsChange: (value: boolean) => void
  onEmailChange: (value: boolean) => void
  onFrequenciesChange: (frequencies: FrequencyRowState[]) => void
}) {
  const [deleteTargetKey, setDeleteTargetKey] = useState<string | null>(null)

  const updateFrequencyHours = (clientKey: string, value: number) => {
    onFrequenciesChange(
      frequencies.map((frequency) =>
        frequency.clientKey === clientKey
          ? {...frequency, hoursBeforeAppointment: Math.max(1, value || 1)}
          : frequency
      )
    )
  }

  const toggleEdit = (clientKey: string) => {
    onFrequenciesChange(
      frequencies.map((frequency) =>
        frequency.clientKey === clientKey
          ? {...frequency, isEditing: !frequency.isEditing}
          : frequency
      )
    )
  }

  const addFrequency = () => {
    onFrequenciesChange([
      ...frequencies,
      {
        clientKey: createClientKey(),
        hoursBeforeAppointment: 24,
        isEditing: true,
      },
    ])
  }

  const confirmDelete = () => {
    if (!deleteTargetKey) return

    if (frequencies.length <= 1) {
      toaster('warning', 'At least one reminder frequency is required')
      setDeleteTargetKey(null)
      return
    }

    onFrequenciesChange(frequencies.filter((frequency) => frequency.clientKey !== deleteTargetKey))
    setDeleteTargetKey(null)
  }

  return (
    <div className='row mb-6'>
      <div className='col-lg-6'>
        <div className='mb-5'>
          <label className='form-label fw-bold fs-6 mb-3'>{title}</label>

          <div className='d-flex align-items-center gap-6 mb-3'>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-gray-700 fs-7 fw-semibold'>SMS</span>
              <div className='form-check form-switch form-check-custom form-check-solid mb-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={smsEnabled}
                  onChange={(e) => onSmsChange(e.target.checked)}
                  disabled={!canEdit}
                />
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-gray-700 fs-7 fw-semibold'>Email</span>
              <div className='form-check form-switch form-check-custom form-check-solid mb-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={emailEnabled}
                  onChange={(e) => onEmailChange(e.target.checked)}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>

          {frequencies.map((frequency) => {
            const isEditable = canEdit && frequency.isEditing

            return (
            <div key={frequency.clientKey} className='d-flex align-items-center gap-2 mb-3'>
              <input
                type='number'
                className={`form-control ${isEditable ? 'form-control-white border border-gray-300' : 'form-control-solid'}`}
                value={frequency.hoursBeforeAppointment}
                onChange={(e) =>
                  updateFrequencyHours(frequency.clientKey, parseInt(e.target.value, 10) || 1)
                }
                min={1}
                readOnly={!isEditable}
                autoFocus={isEditable}
              />
              <button
                type='button'
                className={`btn btn-icon btn-sm flex-shrink-0 ${isEditable ? 'btn-success' : 'btn-light-success'}`}
                onClick={() => toggleEdit(frequency.clientKey)}
                disabled={!canEdit}
                title={isEditable ? 'Done editing' : 'Edit'}
              >
                <i className='bi bi-pencil' />
              </button>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light-danger flex-shrink-0'
                onClick={() => setDeleteTargetKey(frequency.clientKey)}
                disabled={!canEdit || frequencies.length <= 1}
                title='Delete'
              >
                <i className='bi bi-trash3' />
              </button>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light-warning flex-shrink-0'
                onClick={addFrequency}
                disabled={!canEdit}
                title='Add reminder frequency'
              >
                <i className='bi bi-plus-lg' />
              </button>
            </div>
            )
          })}

          <div className='text-muted fs-7 mt-2'>{helperText}</div>
        </div>
      </div>

      {deleteTargetKey && (
        <DeleteConfirmationModal
          title='Delete Reminder Frequency'
          content='Are you sure you want to delete this reminder frequency? This change will apply to newly confirmed appointments after you save settings.'
          setOpen={(open) => {
            if (!open) setDeleteTargetKey(null)
          }}
          onSubmit={confirmDelete}
        />
      )}
    </div>
  )
}

export function InterpreterReminderSettings() {
  const access = useFeatureAccess('emails')
  const canEdit = access === 'edit'

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const [osiSmsEnabled, setOsiSmsEnabled] = useState(false)
  const [osiEmailEnabled, setOsiEmailEnabled] = useState(false)
  const [osiFrequencies, setOsiFrequencies] = useState<FrequencyRowState[]>([])
  const [visSmsEnabled, setVisSmsEnabled] = useState(false)
  const [visEmailEnabled, setVisEmailEnabled] = useState(false)
  const [visFrequencies, setVisFrequencies] = useState<FrequencyRowState[]>([])

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get<SettingsResponse>(`${API_URL}/interpreter-reminder-settings`)
      const data = response.data

      setIsEnabled(data.isEnabled)
      setOsiSmsEnabled(data.osi.smsEnabled)
      setOsiEmailEnabled(data.osi.emailEnabled)
      setOsiFrequencies(mapFrequencies(data.osi.frequencies))
      setVisSmsEnabled(data.vis.smsEnabled)
      setVisEmailEnabled(data.vis.emailEnabled)
      setVisFrequencies(mapFrequencies(data.vis.frequencies))
    } catch (error) {
      console.error('Error fetching interpreter reminder settings:', error)
      toaster('error', 'Failed to load interpreter reminder settings')
    } finally {
      setIsLoading(false)
    }
  }

  const validateFrequencies = (frequencies: FrequencyRowState[], label: string) => {
    if (!frequencies.length) {
      toaster('error', `${label} must have at least one reminder frequency`)
      return false
    }

    const duplicateHours = frequencies
      .map((frequency) => frequency.hoursBeforeAppointment)
      .filter((hours, index, all) => all.indexOf(hours) !== index)

    if (duplicateHours.length) {
      toaster('error', `${label} reminder frequencies must be unique`)
      return false
    }

    return true
  }

  const buildPayload = () => ({
    isEnabled,
    osi: {
      smsEnabled: osiSmsEnabled,
      emailEnabled: osiEmailEnabled,
      frequencies: osiFrequencies.map((frequency) => ({
        id: frequency.id,
        hoursBeforeAppointment: frequency.hoursBeforeAppointment,
      })),
    },
    vis: {
      smsEnabled: visSmsEnabled,
      emailEnabled: visEmailEnabled,
      frequencies: visFrequencies.map((frequency) => ({
        id: frequency.id,
        hoursBeforeAppointment: frequency.hoursBeforeAppointment,
      })),
    },
  })

  const handleSave = async () => {
    if (!validateFrequencies(osiFrequencies, 'OSI')) return
    if (!validateFrequencies(visFrequencies, 'VIS')) return

    setIsSaving(true)
    try {
      await axios.put(`${API_URL}/interpreter-reminder-settings`, buildPayload())
      toaster('success', 'Interpreter reminder settings saved successfully')
      await fetchSettings()
    } catch (error: any) {
      console.error('Error saving interpreter reminder settings:', error)
      toaster('error', error?.response?.data?.message ?? 'Failed to save interpreter reminder settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-10'>
        <span className='spinner-border spinner-border-lg'></span>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body'>
        <div className='row mb-6'>
          <div className='col-lg-6'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3'>
                Enable Interpreter Reminder Emails/SMS
              </label>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  id='interpreterReminderEnabled'
                  disabled={!canEdit}
                />
                <label
                  className='form-check-label text-gray-600'
                  htmlFor='interpreterReminderEnabled'
                >
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
              <div className='text-muted fs-7 mt-2'>
                When enabled, interpreters will receive email/sms reminders regarding the appointment.
              </div>
            </div>
          </div>
        </div>

        <FrequencySection
          title='OSI - Reminder Frequency (Hours)'
          helperText='How often to send reminder emails/sms (in hours) before the appointment.'
          smsEnabled={osiSmsEnabled}
          emailEnabled={osiEmailEnabled}
          frequencies={osiFrequencies}
          canEdit={canEdit}
          onSmsChange={setOsiSmsEnabled}
          onEmailChange={setOsiEmailEnabled}
          onFrequenciesChange={setOsiFrequencies}
        />

        <FrequencySection
          title='VIS - Reminder Frequency (Hours)'
          helperText='How often to send reminder emails/sms (in hours) before the appointment.'
          smsEnabled={visSmsEnabled}
          emailEnabled={visEmailEnabled}
          frequencies={visFrequencies}
          canEdit={canEdit}
          onSmsChange={setVisSmsEnabled}
          onEmailChange={setVisEmailEnabled}
          onFrequenciesChange={setVisFrequencies}
        />

        {canEdit && (
          <div className='row'>
            <div className='col-lg-6'>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
